import * as React from "react";
import {
  Box,
  Button,
  Field as ChakraField,
  HStack,
  Input,
  Text,
  VStack,
  RadioGroup,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  Formik,
  Form,
  Field,
  FieldProps,
  FormikProps,
  FormikHelpers,
} from "formik";

import {
  complaintStatusRequest,
  complaintStatusReset,
  searchStatusRequest,
  searchStatusReset,
} from "store/bbps/Complaint/bbpsComplaintActions";
import { RootState } from "store/rootReducer";
import { BbpsComplainLayout } from "layouts/BbpsComplainLayout/BbpsComplainLayout";
import BbpsComplaintSearch from "./BbpsComplaintSearch";
import BbpsComplaintList from "./BbpsComplaintList";

type ComplaintStatusMode = "orderId" | "txnRefId";

type ViewComplaintStatusFormValues = {
  mode: ComplaintStatusMode;
  orderId: string;
  txnRefId: string;
};

const FloatingInputField: React.FC<
  FieldProps & {
    label: string;
    maxLength?: number;
    required?: boolean;
  }
> = ({ label, field, form, maxLength, required }) => {
  const [focus, setFocus] = React.useState(false);
  const value = String(field.value ?? "");
  const active = focus || value.length > 0;

  const error =
    (form.touched as any)?.[field.name] && (form.errors as any)?.[field.name]
      ? String((form.errors as any)[field.name])
      : undefined;

  return (
    <ChakraField.Root invalid={!!error}>
      <Box position="relative">
        <Input
          {...field}
          value={value}
          maxLength={maxLength}
          onFocus={() => setFocus(true)}
          onBlur={(e) => {
            setFocus(false);
            field.onBlur(e);
          }}
          h="52px"
          pt="18px"
          bg="white"
          borderColor="blackAlpha.200"
          _hover={{ borderColor: "blackAlpha.300" }}
          _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px" }}
          required={required}
          autoComplete="new-password"
        />

        <Text
          position="absolute"
          left="12px"
          top={active ? "8px" : "50%"}
          transform={active ? "translateY(0)" : "translateY(-50%)"}
          fontSize={active ? "xs" : "sm"}
          color={active ? "gray.600" : "gray.500"}
          pointerEvents="none"
          transition="all 120ms ease"
          bg="white"
          px="6px"
        >
          {label}
        </Text>
      </Box>

      {error ? <ChakraField.ErrorText>{error}</ChakraField.ErrorText> : null}
    </ChakraField.Root>
  );
};

const Row: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <HStack justify="space-between">
      <Text fontSize="sm" color="gray.600">
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="gray.800"
        textAlign="right"
      >
        {value}
      </Text>
    </HStack>
  );
};

export const ViewComplaintStatusPage: React.FC = () => {
  const dispatch = useDispatch();
  const statusBox = useSelector((s: RootState) => s.bbpsComplaint.status);
  const searchStatusState = useSelector((s: RootState) => s.bbpsComplaint.searchStatus);


  React.useEffect(() => {
    return () => {
      dispatch(complaintStatusReset());
      dispatch(searchStatusReset());
    };
  }, [dispatch]);

  const initialValues: ViewComplaintStatusFormValues = React.useMemo(
    () => ({
      mode: "orderId",
      orderId: "",
      txnRefId: "",
    }),
    [],
  );

  const validate = (values: ViewComplaintStatusFormValues) => {
    const errors: Partial<Record<keyof ViewComplaintStatusFormValues, string>> =
      {};
    const mode = values.mode;
    const orderId = values.orderId?.trim() ?? "";
    const txnRefId = values.txnRefId?.trim() ?? "";

    // Validate based on mode
    if (mode === "orderId") {
      if (!orderId) errors.orderId = "Transaction ID is required.";
      if (orderId.length > 30)
        errors.orderId = "Transaction ID must be <= 30 characters.";
    } else {
      if (!txnRefId) errors.txnRefId = "Transaction Reference ID is required.";
      if (txnRefId.length > 50)
        errors.txnRefId = "Transaction Reference ID must be <= 50 characters.";
    }

    return errors;
  };

  const onSubmit = (
    values: ViewComplaintStatusFormValues,
    actions: FormikHelpers<ViewComplaintStatusFormValues>,
  ) => {
    // IMPORTANT: Formik sets isSubmitting=true before calling onSubmit,
    // so if we return early, we must set it back to false.
    if (statusBox.loading) {
      actions.setSubmitting(false);
      return;
    }

    dispatch(complaintStatusReset());

    // Send request based on mode
    if (values.mode === "orderId") {
      dispatch(complaintStatusRequest({ orderId: values.orderId.trim() }));
    } else {
      dispatch(
        complaintStatusRequest({ txnReferenceId: values.txnRefId.trim() }),
      );
    }

    // Redux/Saga is the async owner; release Formik immediately
    actions.setSubmitting(false);
  };

  return (
    <BbpsComplainLayout title="View Complaint Status" backTo="/bbps">
      <Formik<ViewComplaintStatusFormValues>
        initialValues={initialValues}
        validate={validate}
        validateOnBlur
        validateOnChange
        onSubmit={onSubmit}
      >
        {(fp: FormikProps<ViewComplaintStatusFormValues>) => {
          const canSubmit =
            fp.isValid &&
            !statusBox.loading &&
            (fp.values.mode === "orderId"
              ? fp.values.orderId.trim().length > 0
              : fp.values.txnRefId.trim().length > 0);

          return (
            <Form>
              <VStack align="stretch" gap={4}>
                {/* Mode Selection */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Check Status By
                  </Text>
                  <RadioGroup.Root
                    value={fp.values.mode}
                    onValueChange={(details) => {
                      const newMode = details.value as ComplaintStatusMode;
                      fp.setFieldValue("mode", newMode);

                      // Clear input fields when mode changes
                      fp.setFieldValue("orderId", "");
                      fp.setFieldValue("txnRefId", "");

                      // Reset touched state for clean UX
                      fp.setTouched({});

                      // Clear any previous status results
                      dispatch(complaintStatusReset());
                    }}
                  >
                    <HStack gap={6}>
                      <RadioGroup.Item value="orderId">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          Order ID
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                      <RadioGroup.Item value="txnRefId">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          Transaction Ref ID
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                    </HStack>
                  </RadioGroup.Root>
                </Box>

                {/* Order ID or TxnRefId based on mode */}
                {fp.values.mode === "orderId" ? (
                  <Field name="orderId">
                    {(p: FieldProps) => (
                      <FloatingInputField
                        {...p}
                        label="Order ID"
                        maxLength={30}
                        required
                      />
                    )}
                  </Field>
                ) : (
                  <Field name="txnRefId">
                    {(p: FieldProps) => (
                      <FloatingInputField
                        {...p}
                        label="Transaction Reference ID"
                        maxLength={50}
                        required
                      />
                    )}
                  </Field>
                )}

                <Button
                  type="submit"
                  h="48px"
                  rounded="lg"
                  loading={statusBox.loading}
                  disabled={!canSubmit}
                >
                  View Status
                </Button>

                {statusBox.error && (
                  <Box
                    p={3}
                    rounded="lg"
                    bg="red.50"
                    borderWidth="1px"
                    borderColor="red.100"
                  >
                    <Text fontWeight="semibold" color="red.700">
                      {statusBox.error}
                    </Text>
                    {/* {statusBox.statusCode && (
                      <Text fontSize="sm" color="red.600" mt={1}>
                        StatusCode: {statusBox.statusCode}
                      </Text>
                    )} */}
                  </Box>
                )}

                {statusBox.data && (
                  <Box
                    p={4}
                    rounded="xl"
                    bg="blue.50"
                    borderWidth="1px"
                    borderColor="blue.100"
                  >
                    <Text fontWeight="bold" color="blue.800">
                      Complaint Status
                    </Text>

                    <VStack align="stretch" mt={3} gap={2}>
                      <Row label="Ticket ID" value={statusBox.data.ticketId} />
                      <Row
                        label="Ticket Status"
                        value={statusBox.data.ticketStatus}
                      />
                      <Row
                        label="Reason"
                        value={statusBox.data.responseReason}
                      />
                      <Row
                        label="Ticket Type"
                        value={statusBox.data.ticketType}
                      />
                      {statusBox.data.description && (
                        <Row
                          label="Description"
                          value={statusBox.data.description}
                        />
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Form>
          );
        }}
      </Formik>

      <Box h={10} />

      <BbpsComplaintSearch 
        loading={searchStatusState.loading}
        onSearch={(filters) => {
          dispatch(searchStatusRequest(filters));
        }}
        onReset={() => {
          dispatch(searchStatusReset());
        }}
      />

      <BbpsComplaintList 
        items={searchStatusState.data?.complaints || []}
        loading={searchStatusState.loading}
        error={searchStatusState.error}
      />
    </BbpsComplainLayout>
  );
};

export default ViewComplaintStatusPage;
